import { Button, Modal, Transfer, Typography, message } from 'antd';
import { formatDate } from 'services/date';
import { useCan, useCreate, useList } from '@refinedev/core';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { IClassroom } from 'interfaces/classrooms';
import type { TransferItem } from 'antd/es/transfer';

interface ClassroomsDisciplineUpdateModalProps {
  open: boolean;
  onCancel: () => void;
  onUpdate: () => void;
}

export const ClassroomsDisciplineUpdateModal = ({
  open,
  onCancel,
  onUpdate
}: ClassroomsDisciplineUpdateModalProps) => {
  const [classrooms, setClassrooms] = useState<IClassroom[]>([]);
  const [selectedClassroomIds, setSelectedClassroomIds] = useState<string[]>(
    []
  );

  const { id: discipline_id } = useParams<{ id: string }>();
  const { mutate } = useCreate();
  const { data: canListClassrooms } = useCan({
    resource: 'classrooms',
    action: 'list'
  });

  const { data: classroomsData } = useList({
    resource: 'classrooms',
    queryOptions: {
      enabled: open && Boolean(canListClassrooms?.can)
    },
    pagination: {
      mode: 'server',
      pageSize: 9999
    },
    filters: [
      {
        field: 'filter[by_discipline_id]',
        operator: 'eq',
        value: discipline_id
      },
      {
        field: 'filter[unfinished]',
        operator: 'eq',
        value: true
      }
    ]
  });

  useEffect(() => {
    if (classroomsData?.data) {
      setClassrooms(classroomsData.data as IClassroom[]);
    }
  }, [classroomsData]);

  const transferData: TransferItem[] = classrooms.map(classroom => ({
    key: classroom.id,
    title: classroom.code,
    description: `${formatDate(
      classroom.starts_at,
      'DD/MM/YYYY'
    )} > ${formatDate(classroom.ends_at, 'DD/MM/YYYY')}`
  }));

  const handleUpdate = () => {
    mutate(
      {
        resource: `graduate/disciplines/${discipline_id}/sync_content`,
        values: { classroom_ids: selectedClassroomIds },
        successNotification: false
      },
      {
        onSuccess: onUpdate,
        onError: () => {
          message.error('Erro ao tentar atualizar turmas');
        }
      }
    );
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        title="Atualizar conteúdo da disciplina nas turmas"
        width={700}
      >
        <Typography.Paragraph type="secondary">
          Escolha as turmas que devem receber as atualizações feitas nesta
          disciplina. <br />
          <span style={{ fontSize: 12 }}>
            Turmas finalizadas não podem ser atualizadas
          </span>
        </Typography.Paragraph>

        <Transfer
          dataSource={transferData}
          targetKeys={selectedClassroomIds}
          onChange={keys => setSelectedClassroomIds(keys.map(String))}
          showSearch={true}
          filterOption={(inputValue, item) => {
            const titleMatch = item.title
              ? item.title.toLowerCase().includes(inputValue.toLowerCase())
              : false;
            const descMatch = item.description
              ? item.description
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              : false;
            return titleMatch || descMatch;
          }}
          showSelectAll={false}
          listStyle={{ width: 'calc(50% - 8px)', height: 360 }}
          render={item => (
            <div>
              <div>{item.title}</div>
              <div style={{ fontSize: 12, color: '#888' }}>
                {item.description}
              </div>
            </div>
          )}
          titles={['Turmas da disciplina', 'Turmas selecionadas']}
          style={{ marginBottom: 24 }}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8
          }}
        >
          <Button onClick={onCancel}>Voltar</Button>
          <Button
            type="primary"
            onClick={handleUpdate}
            disabled={selectedClassroomIds.length === 0}
          >
            Atualizar turmas
          </Button>
        </div>
      </Modal>
    </>
  );
};
